import { useState } from "react";
import { TextInput, Button, SelectInput } from "../../../../components";
import { Link } from "react-router-dom";
import { validateEmail, validatePhone } from "../../../../assets/js/validation";
import {
  allCities,
  allCountries,
  allStates,
} from "../../../../constant/countryStateCityList";
import { GAEventTrack } from "../../../../utils/hooks/GA_events";
import UploadLogo from "./UploadLogo";

const SchoolRegisterStep2 = ({
  schoolAddress,
  setSchoolAddress,
  address,
  setAddress,
  schoolPhone,
  setSchoolPhone,
  logoUrl,
  setLogoUrl,
  logoRef,
  setLogoRef,
  principalName,
  setPrincipalName,
  principalEmail,
  setPrincipalEmail,
  principalPhone,
  setPrincipalPhone,
  onBack,
  onSubmit,
  step1Data,
  loading,
  error,
  setError,
}) => {
  const [validationErrors, setValidationErrors] = useState({});

  const resetFieldError = (field) => {
    setValidationErrors((prev) => ({ ...prev, [field]: null }));
    setError(null);
  };

  const validateStep2 = () => {
    const errors = {};

    // School address validations
    if (!schoolAddress.trim()) {
      errors.schoolAddress = "School Address is required.";
    }
    if (!address?.country) {
      errors.country = "Country is required.";
    }
    if (!address?.state) {
      errors.state = "State is required.";
    }
    if (!address?.city) {
      errors.city = "City is required.";
    }
    if (!address?.pincode) {
      errors.pincode = "Pincode is required.";
    }
    if (address?.pincode && address.pincode.length < 5) {
      errors.pincode = "Please enter a valid pincode";
    }

    // School phone validation
    // if (!schoolPhone.trim()) {
    //   errors.schoolPhone = "School Phone No. is required.";
    // }
    if (schoolPhone && !validatePhone(schoolPhone)) {
      errors.schoolPhone = "Please enter a valid 10 digit school phone number";
    }

    // Principal validations (all optional)
    if (principalEmail && !validateEmail(principalEmail)) {
      errors.principalEmail = "Please enter a valid email id.";
    }
    if (principalPhone && !validatePhone(principalPhone)) {
      errors.principalPhone = "Please enter a valid 10 digit phone number";
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validateStep2();

    if (Object.keys(errors).length > 0) {
      // Focus first error field
      const firstErrorField = Object.keys(errors)[0];
      const formEl = e.target;

      // Try native target lookup first
      const nativeField = formEl[firstErrorField];
      if (nativeField && typeof nativeField.focus === "function") {
        nativeField.focus();
      } else {
        // Fallback for react-select: focus the input via inputId
        const idMap = {
          country: "country",
          state: "state",
          city: "city",
        };
        const targetId = idMap[firstErrorField] || firstErrorField;
        const selectInput = document.getElementById(targetId);
        if (selectInput) {
          // react-select renders an input sibling; focus container then inner input
          const innerInput = selectInput.parentElement?.querySelector(
            "input.select__input"
          );
          if (innerInput && typeof innerInput.focus === "function") {
            innerInput.focus();
          } else if (typeof selectInput.focus === "function") {
            selectInput.focus();
          }
          // Ensure it is visible
          selectInput.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      setValidationErrors(errors);
      return;
    }

    GAEventTrack({
      event: "school-reg-step-2-submit",
      data: { page: "school-register-step2" },
    });

    // Prepare step 2 data
    const step2Data = {
      schoolAddress: schoolAddress.trim(),
      country: address?.country,
      state: address?.state,
      city: address?.city,
      pincode: address?.pincode,
      schoolPhone: schoolPhone.trim(),
      logoUrl: logoUrl,
      principalName: principalName.trim(),
      principalEmail: principalEmail.trim(),
      principalPhone: principalPhone.trim(),
    };

    onSubmit(step2Data);
  };

  // DEV ONLY: Fill step 2 form with test data for development
  // const fillTestData = () => {
  //   console.log("fillTestData Step 2 function called");
  //   setSchoolAddress("123 Main Street, Downtown Area");
  //   setAddress({
  //     country: { id: "IN", value: "India", label: "India" },
  //     state: { id: "MH", value: "Maharashtra", label: "Maharashtra" },
  //     city: { id: "MUM", value: "Mumbai", label: "Mumbai" },
  //     pincode: "400001",
  //   });
  //   setSchoolPhone("9876543210");
  //   setPrincipalName("Dr. Sarah Johnson");
  //   setPrincipalEmail("principal@abcschool.edu");
  //   setPrincipalPhone("9876543211");
  //   setValidationErrors({});
  //   setError(null);
  //   console.log("Step 2 test data filled successfully");
  // };

  return (
    <div className="step2-container">
      <form className="wid-100" noValidate onSubmit={handleSubmit}>
        {/* School Address Information */}
        <div className="form-outer-container bg-blue-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800 mb-4">
            School Address Information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group sm:col-span-2">
              <label
                htmlFor="schoolAddress"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Address <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="text"
                placeholderText="Enter complete school address"
                inputName="schoolAddress"
                isRequired={true}
                idName="schoolAddress"
                inputValue={schoolAddress}
                handleChange={(e) => {
                  setSchoolAddress(e.target.value);
                  resetFieldError("schoolAddress");
                }}
                error={validationErrors?.schoolAddress}
              />
            </div>

            <div className="form-group select-wrapper">
              <label
                htmlFor="country"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Country <span className="text-red-500"> * </span>
              </label>
              <SelectInput
                inputName="country"
                idName="country"
                placeholderText="Select Country"
                selectOptions={allCountries}
                value={address?.country}
                noOptionsMessage={() => "No Country Found"}
                error={validationErrors?.country}
                handleChange={(selectedOptions) => {
                  resetFieldError("country");
                  setAddress({
                    ...address,
                    country: selectedOptions,
                    state: null,
                    city: null,
                  });
                }}
              />
            </div>

            <div className="form-group select-wrapper">
              <label
                htmlFor="state"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                State <span className="text-red-500"> * </span>
              </label>
              <SelectInput
                inputName="state"
                idName="state"
                placeholderText="Select State"
                selectOptions={allStates(address?.country?.id)}
                value={address?.state}
                noOptionsMessage={() =>
                  !address?.country
                    ? "Please Select Country First"
                    : "No States Found"
                }
                error={validationErrors?.state}
                handleChange={(selectedOptions) => {
                  resetFieldError("state");
                  setAddress({
                    ...address,
                    state: selectedOptions,
                    city: null,
                  });
                }}
                disabled={!address?.country}
              />
            </div>

            <div className="form-group select-wrapper">
              <label
                htmlFor="city"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                City <span className="text-red-500"> * </span>
              </label>
              <SelectInput
                inputName="city"
                idName="city"
                placeholderText="Select City"
                selectOptions={allCities(
                  address?.country?.id,
                  address?.state?.id
                )}
                value={address?.city}
                noOptionsMessage={() =>
                  !address?.country
                    ? "Please Select Country First"
                    : !address?.state
                    ? "Please Select State First"
                    : "No City Found"
                }
                error={validationErrors?.city}
                handleChange={(selectedOption) => {
                  resetFieldError("city");
                  setAddress({ ...address, city: selectedOption });
                }}
                disabled={!address?.state}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="pincode"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Pincode <span className="text-red-500"> * </span>
              </label>
              <TextInput
                type="text"
                placeholderText="Enter pincode"
                inputName="pincode"
                isRequired={true}
                idName="pincode"
                inputValue={address?.pincode || ""}
                handleChange={(e) => {
                  resetFieldError("pincode");
                  setAddress({ ...address, pincode: e.target.value });
                }}
                error={validationErrors?.pincode}
              />
            </div>
          </div>
        </div>

        {/* School Contact & Logo */}
        <div className="form-outer-container bg-orange-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800 mb-4">
            School Contact & Logo
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group sm:col-span-2">
              <label
                htmlFor="schoolPhone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Phone Number
                {/* <span className="text-red-500"> * </span> */}
              </label>
              <TextInput
                type="tel"
                placeholderText="Enter 10 digit school phone number"
                inputName="schoolPhone"
                isRequired={true}
                idName="schoolPhone"
                inputValue={schoolPhone}
                handleChange={(e) => {
                  setSchoolPhone(e.target.value);
                  resetFieldError("schoolPhone");
                }}
                error={validationErrors?.schoolPhone}
              />
            </div>

            <div className="form-group sm:col-span-2">
              <label
                htmlFor="schoolLogo"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                School Logo{" "}
                {/* <span className="text-gray-600 font-normal">(optional)</span> */}
              </label>
              <UploadLogo
                fileName={`school_logo/${step1Data?.schoolName?.replace(
                  " ",
                  "_"
                )}_logo`}
                url={logoUrl}
                setUrl={setLogoUrl}
                fileRef={logoRef}
                setFileRef={setLogoRef}
                loading={loading}
                setLoading={() => {}}
              />
            </div>
          </div>
        </div>

        {/* Principal Information */}
        <div className="form-outer-container bg-orange-50 p-4 sm:p-6 rounded-lg mb-6">
          <h2 className="box-heading text-lg sm:text-xl font-bold text-gray-800 mb-4">
            Principal Information
            <span className="text-gray-600 font-normal text-sm ml-2">
              (optional)
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <label
                htmlFor="principalName"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Principal Name
              </label>
              <TextInput
                type="text"
                placeholderText="Enter principal name"
                inputName="principalName"
                isRequired={false}
                idName="principalName"
                inputValue={principalName}
                handleChange={(e) => {
                  setPrincipalName(e.target.value);
                  resetFieldError("principalName");
                }}
                error={validationErrors?.principalName}
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="principalEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Principal Email
              </label>
              <TextInput
                type="email"
                placeholderText="Enter principal email"
                inputName="principalEmail"
                isRequired={false}
                idName="principalEmail"
                inputValue={principalEmail}
                handleChange={(e) => {
                  setPrincipalEmail(e.target.value);
                  resetFieldError("principalEmail");
                }}
                error={validationErrors?.principalEmail}
              />
            </div>

            <div className="form-group sm:col-span-2">
              <label
                htmlFor="principalPhone"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Principal Phone
              </label>
              <TextInput
                type="tel"
                placeholderText="Enter 10 digit phone number"
                inputName="principalPhone"
                isRequired={false}
                idName="principalPhone"
                inputValue={principalPhone}
                handleChange={(e) => {
                  setPrincipalPhone(e.target.value);
                  resetFieldError("principalPhone");
                }}
                error={validationErrors?.principalPhone}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4">
            <span className="error-txt text-red-500 text-sm">{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-4 mt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-end items-center">
            {/* Dev Test Button - Left Side */}
            {/* <button
              type="button"
              onClick={() => {
                console.log("Dev Test Step 2 button clicked");
                fillTestData();
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Dev Test
            </button> */}

            {/* Back and Register Buttons - Right Side */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                version="outline"
                text="Back"
                type="button"
                handleClick={() => {
                  GAEventTrack({
                    event: "school-reg-step-2-back",
                    data: { page: "school-register-step2" },
                  });
                  onBack();
                }}
                isDisabled={loading?.logoUploading || loading?.register}
              />
              <Button
                version="secondary"
                text="Register"
                type="submit"
                isDisabled={loading?.logoUploading || loading?.register}
                loading={loading?.register}
              />
            </div>
          </div>

          <div className="text-sm sm:text-base flex flex-col sm:flex-row gap-1 sm:gap-2">
            <p className="font-medium">Already Have an Account?</p>
            <Link
              to="/login/school"
              className="font-bold hover:underline cursor-pointer text-[#009DFF] hover:text-[#009DFF]"
              onClick={() =>
                GAEventTrack({
                  event: "signin_btn_sch",
                  data: { page: "school-register-step2" },
                })
              }
            >
              Sign In
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SchoolRegisterStep2;
